import { Type } from "class-transformer";
import { SorterService } from "../_services/sorter.service";
import { ComponentCodeConstants } from "../shared/component-constants";
import { MenuConstants } from './../shared/menu-constants';
import { ComponentVariantFromApiModel } from './api-models/component-variant-fromapi';
import { AssortmentModel } from './assortment-model';
import { ChangeModel } from './change-model';
import { ComponentModel } from './component';
import { ComponentVariantModel } from './component-variant';
import { ComponentVariantChangeModel } from './component-variant-change';
import { ConfigurationModel } from './configuration';
import { ConfigurationPlusModel } from "./configuration-plus-model";
import { VariantSizeModel } from './variant-size';

export class ChosenVariantsModel {
	@Type(() => ComponentVariantModel)
	private chosenVariants?: Array<ComponentVariantModel>;
	private chosenButCurrentlyIrrelevantVariants: Array<ComponentVariantModel> | undefined = [];

	constructor(variants: Array<ComponentVariantModel> | undefined, private sorter: SorterService, private getChooseableVariant: (componentUuid: string, variantUuid: string, variantSizeUuid: string) => Promise<Array<ComponentVariantFromApiModel> | undefined>) {
		this.chosenVariants = variants ? [...variants] : undefined;
		this.CheckForNotSelectedVariants();
	}

	// Chooseable variants with optionals
	public get Variants() {
		return this.chosenVariants;
	}

	public get SelectedVariants() {
		return this.chosenVariants?.filter(variant => !variant.NotSelected);
	}

	public get hasEngraving() {
		return this.chosenVariants?.some(x => x.Component.Code == ComponentCodeConstants.Temple && x.EngravingPossible > 0);
	}

	public get Assortments(): Array<AssortmentModel | undefined> {
		const result = new Array<AssortmentModel | undefined>();
		this.Variants?.forEach(x => x.AllAssortments.forEach(t => result.push(t)));
		return result.distinct((x, y) => x?.Code === y?.Code);
	}

	public CheckForNotSelectedVariants() {
		// Note: This method is called during initialization, so direct mutation is acceptable here
		this.chosenVariants?.forEach(variant => {
			if (variant.VariantColor == null && variant.Optional) {
				variant.NotSelected = true;
			}
		});
	}

	public getSelected(component: ComponentModel) {
		return this.Variants?.find(x => x.Component.Id == component.Id);
	}

	public getVariantsWithEngraving() {
		return this.Variants?.filter(x => x.EngravingPossible);
	}

	public SetVariants(variants: Array<ComponentVariantModel>) {
		if (variants == null) {
			throw new Error("variant list cannot be null");
		}

		this.chosenVariants = [...variants];
		this.CheckForNotSelectedVariants();
	}

	public ToggleClipOnOnly(isOnlyClipOn: boolean) {
		if (isOnlyClipOn) {
			// store the currently irrelevant variants
			this.chosenButCurrentlyIrrelevantVariants = this.chosenVariants?.filter(variant => variant.Component.Code != ComponentCodeConstants.Clipon);

			// only select clipon and dependant variants
			let cliponVariants = this.chosenVariants?.filter(variant => variant.Component.Code == ComponentCodeConstants.Clipon) || [];
			const upperRimVariant = this.chosenVariants?.find(variant => variant.Component.Code == ComponentCodeConstants.Upperrim);
			if (upperRimVariant) {
				cliponVariants = [...cliponVariants, upperRimVariant];
			}

			this.chosenVariants = cliponVariants;
		}
		else {
			if (this.chosenButCurrentlyIrrelevantVariants) {
				// Clip-on-only keeps the upper rim active, so restore only components that are not already selected.
				const chosenVariants = [...(this.chosenVariants || [])];

				this.chosenButCurrentlyIrrelevantVariants.forEach(variant => {
					const variantAlreadyChosen = chosenVariants.some(chosenVariant =>
						chosenVariant.Component.Uuid == variant.Component.Uuid
					);

					if (!variantAlreadyChosen) {
						chosenVariants.push(variant);
					}
				});

				this.chosenVariants = chosenVariants;
			}

			this.chosenButCurrentlyIrrelevantVariants = [];
		}
	}

	public isSingleComponent(component: ComponentCodeConstants): boolean {
		return this.Variants?.length == 1 && this.Variants[0].Component.Code == component;
	}

	public UpdateVariants(variants: Array<ComponentVariantModel>) {
		let newChosenVariants = this.chosenVariants ? [...this.chosenVariants] : [];

		variants.forEach(variant => {
			if (!newChosenVariants.some(x => x.Component.Uuid == variant.Component.Uuid)) {
				newChosenVariants.push(variant);
			}
		});

		newChosenVariants = newChosenVariants.filter(chosenVariant =>
			variants.some(x => x.Component.Uuid == chosenVariant.Component.Uuid)
		);

		this.chosenVariants = newChosenVariants;
	}

	public updateConfiguration(configuration: ConfigurationPlusModel): ChangeModel {
		return this.internalUpdateConfiguration(configuration.variants, configuration.configurationModel);
	}

	public updateFromConfiguration(configuration: ConfigurationModel, variants: Array<ComponentVariantModel>): ChangeModel {
		const variantUpdateModels = this.internalUpdateConfiguration(variants, configuration);
		return variantUpdateModels;
	}

	private internalUpdateConfiguration(variants: Array<ComponentVariantModel>, configuration: ConfigurationModel): ChangeModel {
		const variantUpdateModels = new Array<ComponentVariantChangeModel>();
		this.Variants?.forEach(chosenVariant => {
			// variants directly from customiser is used.
			if (variants && variants.length > 0) {
				const newMatchedVariant = variants.find(x => x.Code == chosenVariant.Code && x.Component.Code == chosenVariant.Component.Code);
				let newVariant = newMatchedVariant ? newMatchedVariant : variants.find(x => x.Component.Code == chosenVariant.Component.Code || x.Component.Uuid == chosenVariant.Component.Uuid);
				if (newVariant) {
					if (!newVariant.Engraving && chosenVariant.Engraving) {
						newVariant.Engraving = chosenVariant.Engraving;
					}

					if (newVariant.VariantColor == null) {
						newVariant.NotSelected = true;
					}

					const variantUpdateModel = new ComponentVariantChangeModel(chosenVariant, newVariant, null);
					variantUpdateModels.push(variantUpdateModel);
				} else if (chosenVariant.Component.Code != ComponentCodeConstants.Clipon) {
					newVariant = Object.assign(new ComponentVariantModel(chosenVariant.Discontinued, chosenVariant.New), chosenVariant);
					newVariant.NotSelected = true;
					newVariant.VariantColor = null;
					const variantUpdateModel = new ComponentVariantChangeModel(chosenVariant, newVariant, null);
					variantUpdateModels.push(variantUpdateModel);
				}
			}
		});

		return new ChangeModel(variantUpdateModels, configuration);
	}

	public async updateVariantDesign(variant: ComponentVariantChangeModel): Promise<ComponentVariantChangeModel> {
		// trying to catch updates in finish...

		// let templeVariant = this.chosenVariants.find(v => v.Component.Code === 'TEMPLE');

		// if (templeVariant) {
		// 	let code = variant.NewComponentVariant.Code;
		// 	let finish = code.substring(code.lastIndexOf(';') + 1)

		// 	let chosenCode = templeVariant.Code;
		// 	let chosenFinish = chosenCode.substring(chosenCode.lastIndexOf(';') + 1)

		// 	if (finish !== chosenFinish) {
		// 		variant.MenuTypesWithWarning.push(MenuConstants.FINISH);
		// 		variant.MenuTypesWithWarning.push(MenuConstants.DESIGN);
		// 	}
		// }

		if (variant.NewComponentVariant?.Component.Code == ComponentCodeConstants.Temple) {
			if (variant.NewComponentVariant.EngravingPossible === 0) {
				variant.NewEngraving = "";
				variant.MenuTypesWithWarning.push(MenuConstants.ENGRAVING);
			}
			else if (variant.NewComponentVariant.Engraving?.length > variant.NewComponentVariant.EngravingPossible) {
				variant.NewEngraving = variant.NewComponentVariant.Engraving.slice(0, variant.NewComponentVariant.EngravingPossible);
				variant.MenuTypesWithWarning.push(MenuConstants.ENGRAVING);
			}
		}

		const currentChosenSize = variant.OldComponentVariant?.VariantSize ? this.chosenVariants?.find(x => x.Uuid == variant.OldComponentVariant?.Uuid)?.VariantSize?.Size : null;

		if (currentChosenSize) {
			// get list of available sizes for the wanted selected variant
			const newSizes = variant.NewSizes.sort(this.sorter.sortBy(VariantSizeModel));

			// if the list contains an item with the same size as the currently selected we select that (from the list)
			// otherwise...
			if (!variant.NewSizes.some(x => x.Size == currentChosenSize)) {
				// filter the new list for all items that has an order that is greater than the order of the currently selected
				// and take the first one
				if (variant.NewComponentVariant)
					variant.NewComponentVariant.VariantSize = newSizes.filter(x => {
						const order = variant?.OldComponentVariant?.VariantSize?.Order;
						return order !== undefined && x.Order > order;
					}
					)[0];

				// otherwise filter the new list for all items that has an order that is smaller than the order of the currently selected
				// and take the last one
				if (variant.NewComponentVariant && variant.NewComponentVariant?.VariantSize == null) {
					variant.NewComponentVariant.VariantSize = newSizes.filter(x => {
						const order = variant?.OldComponentVariant?.VariantSize?.Order;
						return order !== undefined && x.Order < order;
					})[variant.NewSizes.length - 1];
				}

				// otherwise take the one that is marked as default size
				if (variant.NewComponentVariant && variant.NewComponentVariant?.VariantSize == null) {
					variant.NewComponentVariant.VariantSize = newSizes.find(x => x.Default);
				}

				variant.MenuTypesWithWarning.push(MenuConstants.SIZE);
			}
			else {
				if (variant.NewComponentVariant)
					variant.NewComponentVariant.VariantSize = variant.NewSizes.find(x => x.Size == variant.OldComponentVariant?.VariantSize?.Size);
			}

			variant = await this.updateComponentSize(variant);
		}

		return variant;
	}

	public async updateComponentSize(variant: ComponentVariantChangeModel): Promise<ComponentVariantChangeModel> {
		let variants: ComponentVariantFromApiModel[] = []
		if (variant.NewComponentVariant && variant.NewComponentVariant?.VariantSize) {

			variants = await this.getChooseableVariant(variant.NewComponentVariant.Component.Uuid, variant.NewComponentVariant.Uuid,
				variant.NewComponentVariant.VariantSize.Uuid) ?? [];
		}
		const colors = variants != null && variants.length > 0 ? variants[0]?.VariantSizes[0]?.VariantColors : [];

		let nextColor = ComponentVariantFromApiModel.GetNextColor(variant.OldComponentVariant?.VariantColor, colors);

		const currentColorCode = !variant.OldComponentVariant?.VariantColor ? null : variant.OldComponentVariant?.VariantColor.Color?.Code;
		const nextColorCode = !nextColor ? null : nextColor.Color?.Code;

		if (nextColorCode != currentColorCode) {
			variant.MenuTypesWithWarning.push(MenuConstants.COLOR);
		}

		if (variant.NewComponentVariant?.Component.Code === ComponentCodeConstants.Gemstone) {
			const variantSize = variant.NewComponentVariant.VariantSize;
			if (!variantSize && !variant.NewComponentVariant.VariantColor) {
				nextColor = null;
			} else {
				nextColor = colors.filter(v => v.Default)[0] || colors[0]
			}
		}
		if (variant.NewComponentVariant)
			variant.NewComponentVariant.VariantColor = nextColor;
		return variant;
	}

	public updateVariant(variant: ComponentVariantChangeModel): Array<ComponentVariantChangeModel> {
		const variantsToUpdate: Array<ComponentVariantChangeModel> = [variant];

		return variantsToUpdate;
	}

	public static getVariantsWithChanges(chosenVariants?: Array<ComponentVariantModel>, updates?: Array<ComponentVariantChangeModel | null>): Array<ComponentVariantModel> {
		let variants: Array<ComponentVariantModel> = chosenVariants ? [...chosenVariants] : [];

		if (updates != null && updates.length > 0) {
			updates.forEach(variantToUpdate => {
				if (variantToUpdate?.NewComponentVariant) {
					if (variantToUpdate.OldComponentVariant) {
						variantToUpdate.NewComponentVariant.OrderLineUuid = variantToUpdate.OldComponentVariant.OrderLineUuid;
						const index = variants.findIndex(x => x.Uuid == variantToUpdate.OldComponentVariant?.Uuid);
						if (index !== -1) {
							variants = [
								...variants.slice(0, index),
								variantToUpdate.NewComponentVariant,
								...variants.slice(index + 1)
							];
						}
					} else {
						variants = [...variants, variantToUpdate.NewComponentVariant];
					}
				} else {
					variants = variants.filter(x => x.Uuid != variantToUpdate?.OldComponentVariant?.Uuid);
				}
			});
		}

		return variants;
	}
}
